<template>
  <MDBCard class="py-0 border-0">
    <CategoryTop :title="$t('bankstatement.title')" :breadcrumbs-parent-routes="['account']"
      :description="$t('bankstatement.desc')" content-class="px-4 mt-4" leftCol="7" rightCol="5"
      :showSlash="seeRedSlashInPointsOverview()">
      <template #right>
        <MDBTable borderless class="points-overview points-overview--small">
          <tbody>
            <tr class="success-green fw-bold summary-title">
              <td :class="['font-16', { 'nl-points-score-title': isNlTenant }]">
                {{ $t("bankstatement.score") }}
                {{ dates.parseDate(currentDate) }}
              </td>
              <td>
                <div v-if="user.display_points && user?.points.vesting">
                  <div class="font-16"> {{ $t("points.dashboard.dashboardBox.availPoints") }}: {{
                    user.display_points ?
                      user?.points.avail_points : 0 }}
                  </div>
                  <div class="font-16 text-danger">{{ $t("points.dashboard.dashboardBox.awaitingPoints") }}: {{
                    user.display_points ?
                      user?.points.awaiting_points : 0 }}
                  </div>
                </div>
                <div v-else-if="user.display_points && !user?.points.vesting">
                  {{ user.display_points ?
                    user?.points.avail_points : 0 }}
                </div>
              </td>
              <td v-if="showKickbackFeeList()" :class="['font-16', { 'nl-points-score-title': isNlTenant }]">
                {{ $t("kickback.title") }}
              </td>
              <td v-if="showKickbackFeeList()">
                <strong>{{ formatCurrencyEur(user?.kick_back_fees, storedLocale) }}</strong>
              </td>
            </tr>
            <tr class="summary-details">
              <td v-if="showCurrentMonthPointsInfo()">
                {{ showPointsExpirySummary ? pointsFromCurrentYearLabel : `${$t("bankstatement.income")}
                ${getMonth(currentDate.getMonth() + 1)}` }}
              </td>
              <td v-if="showCurrentMonthPointsInfo()">
                <strong>{{
                  user.display_points ? (showPointsExpirySummary ? pointsFromCurrentYear : currentMonthPlus) : 0
                }}</strong>
              </td>

              <td v-if="showKickbackFeeInfo()">
                {{ t("bankstatement.kickbackIncome") }}
                {{ getMonth(currentDate.getMonth() + 1) }}
              </td>
              <td v-if="showKickbackFeeInfo()">
                <strong>{{
                  formatCurrencyEur(
                    user.display_kickbacks ? currentMonthKickbacksPlus : 0,
                    storedLocale
                  )
                }}</strong>
              </td>
            </tr>
            <tr class="summary-details">
              <td v-if="showCurrentMonthPointsSpendings() && !showPointsExpirySummary">
                {{ $t("bankstatement.spending") }}
                {{ getMonth(currentDate.getMonth() + 1) }}
              </td>
              <td v-if="showCurrentMonthPointsSpendings() && !showPointsExpirySummary">
                <strong class="text-danger">{{ user.display_points ? currentMonthMinus : 0 }}
                </strong>
              </td>
              <td v-if="showCurrentMonthPointsInfo() && showPointsExpirySummary">
                {{ pointsAtRiskOfExpiryLabel }}
              </td>
              <td v-if="showCurrentMonthPointsInfo() && showPointsExpirySummary">
                <strong>{{ user.display_points ? pointsAtRiskOfExpiry : 0 }}</strong>
              </td>
              <td v-if="showKickbackFeeInfo() && showCurrentMonthPointsSpendings()">
                {{ $t("bankstatement.spending") }}
                {{ getMonth(currentDate.getMonth() + 1) }}
              </td>
              <td v-if="showKickbackFeeInfo() && showCurrentMonthPointsSpendings()">
                <strong class="text-danger">{{
                  formatCurrencyEur(
                    user.display_kickbacks
                      ? Number(currentMonthKickbacksMinus ?? 0)
                      : 0,
                    storedLocale
                  )
                }}</strong>
              </td>
            </tr>
            <tr class="summary-details">
              <td v-if="showLastMonthPointsInfo() && showCurrentMonthPointsSpendings()">
                {{ $t("bankstatement.lastMonth") }}
                {{ getMonth(currentDate.getMonth()) }}
              </td>
              <td v-if="showLastMonthPointsInfo() && showCurrentMonthPointsSpendings()">
                <strong>{{ user.display_points ? lastMonthPoints : 0 }}
                </strong>
              </td>
              <td v-if="showKickbackFeeInfo() && showCurrentMonthPointsSpendings()">
                {{ $t("bankstatement.lastMonth") }}
                {{ getMonth(currentDate.getMonth()) }}
              </td>
              <td v-if="showKickbackFeeInfo() && showCurrentMonthPointsSpendings()">
                <strong>{{
                  formatCurrencyEur(
                    user.display_kickbacks ? Number(lastMonthKickbacks ?? 0) : 0,
                    storedLocale
                  )
                }}</strong>
              </td>
            </tr>
            <BonusMultiplicatorRows v-if="showBonusMultiplicator" :bonus-data="bonusMultiplicator"
              :current-year="currentYear" :user-bonus-multiplicator-id="user?.bonus_multiplicator_id"
              :output="bonusMultiplicator?.output" :display-kickbacks="user?.display_kickbacks"
              :is-nl-tenant="isNlTenant" />
          </tbody>
        </MDBTable>
      </template>
    </CategoryTop>

    <MDBRow>
      <MDBCol col="12">
        <MDBRow class="points-overview">
          <MDBCol>
            <div class="btn btn-danger text-uppercase px-5" @click="showVoucherModal()" v-if="canSeeVoucherBtn">
              {{ $t('global.buttons.exchangeVoucher') }}
            </div>
            <div class="btn btn-danger text-uppercase px-5" @click="showExchangeKickbackModal()"
              v-if="canSeeKickbackBtn">
              {{ $t('buttons.exchangeKickback') }}
            </div>
          </MDBCol>
        </MDBRow>
      </MDBCol>
      <MDBCol>
        <div class="table-responsive">
          <MDBRow class="points-overview">
            <MDBCol xl="6" md="6" class="mb-2">
              <label for="pointType" class="label px-3 text-uppercase">
                {{ $t("bankstatement.typeFilter") }}
              </label>
              <select class="form-select mb-1" aria-label=".form-select-lg example" v-model="pointType"
                @change="filterResults" id="pointType">
                <option :value="null">
                  {{ $t("global.makeChoice") }}
                </option>
                <option value="bsPlus">BRIDGESTONE PLUS</option>
                <option :value="'bsDe_' + wsh.id" v-for="(wsh, index) in wshSelect" :key="index">
                  {{ wsh.name }}
                </option>
              </select>
            </MDBCol>
            <MDBCol xl="6" md="6" class="mb-2">
              <label for="date" class="label px-3 text-uppercase">
                {{ $t("bankstatement.dateFilter") }}
              </label>
              <VueDatePicker v-model="date" range :locale="datePickerLocale" :enable-time-picker="false"
                :teleport="true" :cancelText="$t('global.buttons.cancel')" :selectText="$t('global.buttons.select')"
                @update:model-value="filterResults" :format="formatDatePicker" id="date"></VueDatePicker>
            </MDBCol>
          </MDBRow>
          <BankStatementTable :table-headers="tableHeaders" :sort-data="sortData" :on-sort="sort" :bank-data="bankData"
            :parse-date="dates.parseDate" :format-source="setSource" :format-season="setSeason"
            :show-points-column="Boolean(user?.display_points)" :show-kickback-column="showKickbackFeeList()" />
          <div v-if="loading">
            <Loader :show="true" spinner="spinner-red.svg" />
          </div>
          <div class="load-more d-flex justify-content-center">
            <div @click="loadMore" class="btn btn-danger text-uppercase px-5">
              {{ $t("bankstatement.loadMore") }}
            </div>
            <button @click="loadAll" class="btn btn-danger text-uppercase px-5" :disabled="loading" type="button">
              {{ $t("global.buttons.all") }}
            </button>
          </div>
        </div>
      </MDBCol>
    </MDBRow>
  </MDBCard>
  <MDBModal v-model="modal" tabindex="-1" centered size="xl">
    <KickbackExchange @close="(e) => showHideModal()" />
  </MDBModal>
  <MDBModal v-model="voucherModal" tabindex="-1" centered size="xl">
    <VoucherModal @close="(e) => showVoucherModal()" @reload="(e) => reloadData()" />
  </MDBModal>
</template>

<script setup>
import CategoryTop from "../General/CategoryTop/index.vue";
import Loader from "../General/Loader/index.vue";
import BankStatementTable from "./PointsOverview/BankStatementTable.vue";
import BonusMultiplicatorRows from "./PointsOverview/BonusMultiplicatorRows.vue";
import KickbackExchange from "./PointsOverview/KickbackExchange.vue";
import VoucherModal from "./PointsOverview/VoucherModal.vue";
import { MDBCard, MDBRow, MDBCol, MDBTable, MDBModal } from "mdb-vue-ui-kit";
import VueDatePicker from "@vuepic/vue-datepicker";
import TokenService from "@/services/storage.service";
import { useTenantConfig } from "@/composables/useTenantConfig";
import {
  userCanSeeKickbackInfo,
  userCanSeeRedSlashInPointsOverview,
  userCanSeeCurrentMonthPointsInfo,
  userCanSeeLastMonthPointsInfo,
  userCanSeeKickbackList,
  userShowCurrentMonthPointsSpendings
} from "@/composables/useProfileFieldCheck";

import { toast } from "vue3-toastify";
import { useStore } from "vuex";
import { computed, ref, reactive, watch, toRaw, onMounted } from "vue";

import { useI18n } from "vue-i18n";
const { t } = useI18n() // Import the translation function from i18n
import dates from "@/plugins/dates";
import getSeasonLabel from "@/helpers/season";
import { formatCurrencyEur } from "@/utils/formatCurrency";
const storedLocale = computed(() => store.getters.getCurrentLocale);
const { tenantConfig } = useTenantConfig();
const { seeRedSlashInPointsOverview } = userCanSeeRedSlashInPointsOverview(tenantConfig);
const { showKickbackFeeInfo } = userCanSeeKickbackInfo(tenantConfig);
const { showKickbackFeeList } = userCanSeeKickbackList(tenantConfig);
const { showCurrentMonthPointsInfo } = userCanSeeCurrentMonthPointsInfo(tenantConfig);
const { showLastMonthPointsInfo } = userCanSeeLastMonthPointsInfo(tenantConfig);
const { showCurrentMonthPointsSpendings } = userShowCurrentMonthPointsSpendings(tenantConfig);
const datePickerLocale = 'en'
const store = useStore();
const tenantId = computed(() => String(store.getters.tenantId || "").toUpperCase());
const isNlTenant = computed(() => tenantId.value === "NL");
const modal = ref(false)
const voucherModal = ref(false)
const user = computed(() => store.getters.getMe);
const page = ref(0);
const bankData = ref([]);
const pointType = ref(null);
const date = ref([]);
const canSeeVoucherBtn = ref(false)
const canSeeKickbackBtn = ref(false)
const tableHeaders = ref([
  {
    label: t("bankstatement.date"),
    field: "date",
    factory: (v) => new Date(v).getTime(),
  },
  {
    label: t("bankstatement.supplySourceReason"),
    field: "source",
  },
  {
    label: t("bankstatement.customer_number"),
    field: "customer_number",
  },
  {
    label: t("bankstatement.article"),
    field: "article",
  },
  {
    label: t("bankstatement.brand"),
    field: "brand",
  },
  {
    label: t("bankstatement.product"),
    field: "product",
  },
  {
    label: t("bankstatement.type"),
    field: "type",
  },
  {
    label: t("bankstatement.inch"),
    field: "dimension",
  },
  {
    label: t("bankstatement.season"),
    field: "saison",
    factory: (v) => {
      return !v ? setSeason("") : setSeason(v);
    },
  },
  {
    label: t("bankstatement.amount"),
    field: "qty",
  },
  {
    label: t("bankstatement.otherActions"),
    field: "provenance",
  },
]);

const sortData = reactive({
  field: "",
  order: "",
  factory: (v) => v,
  random: "",
});

store.dispatch("bankStatement", page.value);
store.dispatch("fetchWshSelect");
store.dispatch('me');

const loading = computed(() => store.getters.getPending);

const bank = computed(() => store.getters.getBankStatementData);

const lastMonthPoints = computed(() => store.getters.getLastMonthPoints);
const currentMonthMinus = computed(() => store.getters.getCurrentMonthMinus);
const currentMonthPlus = computed(() => store.getters.getCurrentMonthPlus);
const pointsFromCurrentYear = computed(
  () => Number(store.getters.getPointsFromCurrentYear ?? 0)
);
const pointsAtRiskOfExpiry = computed(
  () => Number(store.getters.getPointsAtRiskOfExpiry ?? 0)
);
const bonusMultiplicator = computed(() => {
  const byYear = store.getters.getPointBonusByYear;
  const goals = store.getters.getBonusMultiplicator;
  if (byYear != null) {
    return {
      bonusGoals: Array.isArray(goals) ? goals : [],
      output: byYear,
    };
  }
  const raw = TokenService.getTyreStatus();
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      if (parsed?.output != null) {
        return parsed;
      }
    } catch {
      /* ignore invalid cache */
    }
  }
  return null;
});

const lastMonthKickbacks = computed(() => store.getters.getLastMonthKickbacks);
const currentMonthKickbacksMinus = computed(
  () => store.getters.getCurrentMonthKickbacksMinus
);
const currentMonthKickbacksPlus = computed(() =>
  Number(store.getters.getCurrentMonthKickbacksPlus ?? 0)
);
const wshSelect = computed(() => store.getters.getWshSelect);
const showPointsExpirySummary = computed(
  () => Boolean(tenantConfig.value?.showPointsExpirySummary)
);
const showBonusMultiplicator = computed(
  () => Boolean(tenantConfig.value?.showBonusMultiplicator)
);

const currentDate = new Date();
const currentYear = computed(() => currentDate.getFullYear());
const pointsFromCurrentYearLabel = computed(
  () => t("bankstatement.pointsFromCurrentYear", { year: currentYear.value })
);
const pointsAtRiskOfExpiryLabel = computed(
  () => t("bankstatement.pointsAtRiskOfExpiry", { year: currentYear.value })
);
const getMonth = (idx) => {
  let objDate = new Date();
  objDate.setDate(1);
  objDate.setMonth(idx - 1);

  let locale = storedLocale.value
  let month = objDate.toLocaleString(locale, { month: "long" });

  return month;
};

const setSeason = (text) => getSeasonLabel(text, t, "bankstatement.seasons");

const setSource = (data) => {
  if (data == "BSPLUS") {
    return `BRIDGESTONE <span class='text-danger'>PLUS</span>`;
  } else {
    return data;
  }
};

const loadMore = async () => {
  page.value += 1;

  let query;

  const dateFrom =
    date.value && date.value[0] ? dates.formatDateYMD(date.value[0]) : "";
  const dateTo =
    date.value && date.value[1] ? dates.formatDateYMD(date.value[1]) : "";

  if (date.value?.length && pointType.value) {
    query = `source=${pointType.value}&dateFrom=${dateFrom}&dateTo=${dateTo}`;
  } else if (date.value && !pointType.value) {
    query = `dateFrom=${dateFrom}&dateTo=${dateTo}`;
  } else if (pointType.value) {
    query = `source=${pointType.value}`;
  }

  return store.dispatch("bankStatement", { page: page.value, query: query });
};

const filterResults = async () => {
  page.value = 0;
  bankData.value = [];
  let query;

  const dateFrom =
    date.value && date.value[0] ? dates.formatDateYMD(date.value[0]) : "";
  const dateTo =
    date.value && date.value[1] ? dates.formatDateYMD(date.value[1]) : "";

  if (date.value?.length && pointType.value) {
    query = `source=${pointType.value}&dateFrom=${dateFrom}&dateTo=${dateTo}`;
  } else if (date.value && !pointType.value) {
    query = `dateFrom=${dateFrom}&dateTo=${dateTo}`;
  } else if (pointType.value) {
    query = `source=${pointType.value}`;
  }
  store.dispatch("bankStatement", { page: page.value, query: query });
};

const sort = (
  args = {
    field: "",
    order: "",
    factory: (v) => v,
  }
) => {
  sortData.field = args.field;
  sortData.order = args.order || sortData.order !== "down" ? "down" : "up";
  sortData.factory = args.factory
    ? args.factory
    : (v) => {
      if (v == null) return "";
      return v;
    };
  sortData.random = Math.random();
};

watch(bank, (newVal) => {
  if (newVal.length > 0 && page.value == 0) {
    bankData.value = Object.values(toRaw(newVal));
  } else {
    bankData.value.push(...Object.values(toRaw(newVal)));
  }
  sort(toRaw(sortData));
});

watch(
  sortData,
  (newVal) => {
    let sortVal = toRaw(newVal);
    bankData.value.sort((a, b) => {
      if (newVal.order == "up") {
        return sortVal.factory(a[sortVal.field]) >
          sortVal.factory(b[sortVal.field])
          ? 1
          : sortVal.factory(b[sortVal.field]) >
            sortVal.factory(a[sortVal.field])
            ? -1
            : 0;
      } else {
        return sortVal.factory(a[sortVal.field]) <
          sortVal.factory(b[sortVal.field])
          ? 1
          : sortVal.factory(b[sortVal.field]) <
            sortVal.factory(a[sortVal.field])
            ? -1
            : 0;
      }
    });
  },
  {
    deep: true,
  }
);

const formatDatePicker = (date) => {
  return `${dates.parseDate(date[0])} - ${date[1] ? dates.parseDate(date[1]) : ""
    }`;
};

watch(user, async () => {
  canSeeVoucherBtn.value = TokenService.showElement("voucherExchange.see");
  canSeeKickbackBtn.value = TokenService.showElement("kickbackExchange.see");
});

onMounted(async () => {
  if (user.value.display_points) {
    tableHeaders.value.push({
      label: t("bankstatement.totalPoints"),
      field: "points",
    });
  }

  if (
    showKickbackFeeList()
  ) {
    tableHeaders.value.push({
      label: t("kickback.word"),
      field: "kickBackFee",
    });
  }

  canSeeVoucherBtn.value = TokenService.showElement("voucherExchange.see");
  canSeeKickbackBtn.value = TokenService.showElement("kickbackExchange.see");

  if (showBonusMultiplicator.value) {
    await store.dispatch("fetchTyreLimit", {
      year: new Date().getFullYear(),
      force: true,
    });
  }
});

const showExchangeKickbackModal = async () => {
  const currentYear = new Date().getFullYear()
  await store.dispatch('fetchTyreLimit', currentYear)
  showHideModal()
}

const showHideModal = () => {
  modal.value = !modal.value
}

const showVoucherModal = () => {
  voucherModal.value = !voucherModal.value
}

const reloadData = async () => {
  bankData.value = [];
  await store.dispatch("bankStatement", 1);
  await store.dispatch("me");
}

const loadAll = async () => {
  toast(t("toastMessages.waitMoment"), {
    type: "success",
    theme: "colored",
    transition: toast.TRANSITIONS.SLIDE,
    position: toast.POSITION.BOTTOM_RIGHT,
  });
  for (let i = 0; i < 1000; i++) {
    const response = await loadMore()

    if (response.data.length == 0) {
      return true;
    }
  }

}
</script>

<style scoped lang="scss">
@import "../../styles/_variables.scss";
@import "@/styles/points_overview.scss";

.nl-points-score-title {
  color: #000;
  font-size: 21px;
}
</style>
